import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/firebase';
import { verifyEmailLink } from '@/services/emailService';

interface VerifyResult {
  success: boolean;
  userId?: string;
  targetRoute: string;
  data?: Record<string, unknown>;
  customToken?: string;
  message?: string;
}

const EmailRedirectHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEmailLink = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid email link - missing token');
        setIsLoading(false);
        return;
      }

      try {
        const result: VerifyResult = await verifyEmailLink(token);

        if (!result.success) {
          throw new Error(result.message || 'Email link verification failed');
        }

        // If we have a custom token, sign the user in
        if (result.customToken) {
          try {
            await signInWithCustomToken(auth, result.customToken);
          } catch {
            // Continue anyway - the link might still work for non-authenticated content
          }
        }

        // Add a small delay to ensure auth state is updated
        setTimeout(() => {
          // Add email link tracking parameters
          const targetUrl = new URL(result.targetRoute, window.location.origin);
          targetUrl.searchParams.set('email_source', 'email_link');
          targetUrl.searchParams.set('email_campaign', 'secure_link');

          // Add any additional data as query parameters
          if (result.data) {
            Object.entries(result.data).forEach(([key, value]) => {
              if (typeof value === 'string' || typeof value === 'number') {
                targetUrl.searchParams.set(key, String(value));
              }
            });
          }

          navigate(targetUrl.pathname + targetUrl.search, { replace: true });
        }, 1000);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setError(errorMessage);

        // Redirect to home after error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    handleEmailLink();
  }, [searchParams, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <h2 className="mt-6 text-3xl font-extrabold text-neutral-900">
              Processing Email Link
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              Please wait while we verify and redirect you...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-error-600">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-neutral-900">
              Link Error
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {error}
            </p>
            <p className="mt-4 text-sm text-neutral-500">
              Redirecting to homepage...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EmailRedirectHandler;